import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import PropTypes from 'prop-types';
import compose from '../../hoc/compose';
import displayNames from '../displayNames';
import KEYS from '../../common/keyCodes';
import _ from 'lodash';
import React from 'react';
import BaseButton from '../button/button';
import CancelSymbol from '../icons/x.svg.js';
import OkSymbol from '../icons/check.svg.js';
import TextInput from '../textInput/textInput';

var getFirstInvalidValidator = function getFirstInvalidValidator(value, validators) {
  return _.find(validators, function (_ref) {
    var validator = _ref.validator;
    return !validator(value);
  });
};

var InputWithConfirm = function InputWithConfirm(props) {
  var ignoreBlurOnce = false;

  var _React$useState = React.useState(props.value),
      _React$useState2 = _slicedToArray(_React$useState, 2),
      value = _React$useState2[0],
      setValue = _React$useState2[1];

  var _React$useState3 = React.useState(false),
      _React$useState4 = _slicedToArray(_React$useState3, 2),
      editMode = _React$useState4[0],
      setEditMode = _React$useState4[1];

  var _React$useState5 = React.useState(true),
      _React$useState6 = _slicedToArray(_React$useState5, 2),
      isValid = _React$useState6[0],
      setIsValid = _React$useState6[1];

  var _React$useState7 = React.useState(),
      _React$useState8 = _slicedToArray(_React$useState7, 2),
      invalidMessage = _React$useState8[0],
      setInvalidMessage = _React$useState8[1];

  var getClassNames = function getClassNames() {
    return props.getClassName('control-input-with-confirm');
  };

  var setInitialValidState = function setInitialValidState() {
    setValue(props.value);
    var initialInvalidValidator = getFirstInvalidValidator(props.value, props.validators);
    setIsValid(!initialInvalidValidator);
    setInvalidMessage(initialInvalidValidator && initialInvalidValidator.invalidMessage);
  };

  React.useEffect(function () {
    setInitialValidState();
  }, ['value', 'validators']);

  var doCancel = function doCancel() {
    var andCallback = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : _.noop;
    setEditMode(false);
    setInitialValidState();
    andCallback();
    props.onCancel();
  };

  var doApply = function doApply() {
    var andCallback = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : _.noop;
    setEditMode(false);
    andCallback();
    props.onChange(value);
  };

  var onCancelClick = function onCancelClick() {
    doCancel();
  };

  var onApplyClick = function onApplyClick() {
    doApply();
  };

  var onInputFocus = function onInputFocus() {
    setEditMode(true);
  };

  var onInputBlur = function onInputBlur(_ref2) {
    var relatedTarget = _ref2.relatedTarget;

    // We only want to apply/cancel the value in case:
    // 1. We did not come here after a keyDown
    // 2. We did not come here after clicking on apply/cancel button
    if (ignoreBlurOnce) {
      ignoreBlurOnce = false;
    } else if (!(relatedTarget && relatedTarget.classList.contains('input-with-confirm-btn'))) {
      if (props.changeOnBlur) {
        doApply();
      } else {
        doCancel();
      }
    }
  };

  var onInputChange = function onInputChange(newValue) {
    var invalidValidator = getFirstInvalidValidator(newValue, props.validators);
    setValue(newValue);
    setIsValid(!invalidValidator);
    setInvalidMessage(invalidValidator && invalidValidator.invalidMessage);
  };

  var onInputKeyDown = function onInputKeyDown(_ref3, blur) {
    var keyCode = _ref3.keyCode;

    if (keyCode === KEYS.ENTER) {
      doApply(function () {
        ignoreBlurOnce = true;
        blur();
      });
    } else if (keyCode === KEYS.ESC) {
      doCancel(function () {
        ignoreBlurOnce = true;
        blur();
      });
    }
  };

  var getButtonClasses = function getButtonClasses(applyButtonPriority) {
    var basic = 'btn-md btn-round input-with-confirm-btn';

    if (applyButtonPriority.toLowerCase() === 'primary') {
      return basic;
    }

    return 'btn-light ' + basic;
  };

  return /*#__PURE__*/React.createElement("div", {
    "data-aid": props.automationId,
    "data-hook": props.dataHook,
    className: getClassNames()
  }, /*#__PURE__*/React.createElement(TextInput, {
    value: value,
    placeholder: props.placeholder,
    maxLength: props.maxLength,
    focus: props.focus,
    selectOnFocus: props.selectOnFocus,
    blurOnEnterKey: false,
    blurOnEscKey: false,
    onFocus: onInputFocus,
    onBlur: onInputBlur,
    onKeyDown: onInputKeyDown,
    onChange: onInputChange,
    isValid: isValid,
    invalidMessage: invalidMessage
  }), editMode && /*#__PURE__*/React.createElement("div", {
    className: "buttons-container",
    key: "buttonsContainer"
  }, /*#__PURE__*/React.createElement(BaseButton, {
    automationId: "input-with-confirm-btn-cancel",
    className: 'btn-light btn-md btn-round input-with-confirm-btn',
    onClick: onCancelClick,
    key: "cancel"
  }, /*#__PURE__*/React.createElement(CancelSymbol, null)), /*#__PURE__*/React.createElement(BaseButton, {
    automationId: "input-with-confirm-btn-apply",
    className: getButtonClasses(props.applyButtonPriority),
    disabled: !isValid,
    onClick: onApplyClick,
    key: "apply"
  }, /*#__PURE__*/React.createElement(OkSymbol, null))));
};

InputWithConfirm.displayName = displayNames.INPUT_WITH_CONFIRM;
InputWithConfirm.propTypes = {
  /**
   * Automation ID - for testing purposes, please use dataHook instead
   * @deprecated
   */
  automationId: PropTypes.string,

  /** Textual value for input field */
  value: PropTypes.string,

  /** Placeholder */
  placeholder: PropTypes.string,

  /** Maximum length of input field */
  maxLength: PropTypes.number,

  /** Is input field focused on component first render */
  focus: PropTypes.bool,

  /** Is the input field content automatically selected on focus */
  selectOnFocus: PropTypes.bool,

  /** Should a change or cancel event be triggered on clicking outside of the component */
  changeOnBlur: PropTypes.bool,

  /** On change callback */
  onChange: PropTypes.func,

  /** On cancel callback */
  onCancel: PropTypes.func,

  /** List of validators */
  validators: PropTypes.arrayOf(PropTypes.shape({
    validator: PropTypes.func.isRequired,
    invalidMessage: PropTypes.string.isRequired
  })),

  /** Priority of the apply button */
  applyButtonPriority: PropTypes.oneOf(['primary', 'secondary'])
};
InputWithConfirm.defaultProps = {
  value: '',
  placeholder: '',
  focus: false,
  selectOnFocus: false,
  changeOnBlur: true,
  onChange: _.noop,
  onCancel: _.noop,
  validators: [],
  applyButtonPriority: 'secondary'
};
export default compose(InputWithConfirm);